import React, { useContext, useEffect, useState } from 'react';
import { Alert, AlertIcon, Box, Center, Image, Text, useColorMode, useColorModeValue } from '@chakra-ui/react';
import Router from 'next/router';
import withLogger, { Logger } from '../libs/logger';
import CredentialsForm from '../components/credentialsForm';
import LoginForm from '../components/loginForm';
import VerifyCodeForm from '../components/verifyCodeForm';
import NewPasswordForm from '../components/newPasswordForm';
import ForgotPasswordForm from '../components/forgotPasswordForm';
import ResetPasswordForm from '../components/resetPasswordForm';
import { AuthContext } from '../context/authContext';
import { EventStateContext } from '../context/EventContext';
import { AnalyticsStateContext } from '../context/analyticsContext';
import { AccessLevel, Action, Category } from '../libs/analytics';
import { EnhancedAnalyticsContext } from '../context/enhancedAnalyticsContext';

interface LoginProps {
  log: Logger;
}

// eslint-disable-next-line no-shadow
enum FormState {
  ENTER_CREDENTIALS,
  LOGIN,
  NEW_PASSWORD,
  CONFIRM_USER,
  FORGOT_PASSWORD,
  RESET_PASSWORD
}

function Login({ log }: LoginProps) {
  const { bgColor, bgImg, darkMode, bannerImg, bgRepeat, loginType, loginRequired } = useContext(EventStateContext);
  const { setColorMode } = useColorMode();
  // setColorMode(darkMode ? 'dark' : 'light');
  const [error, setError] = useState('');
  const [tmpUser, setTmpUser]: [any, any] = useState({});
  const { setUserSession } = useContext(AuthContext as any);
  const { signUpEvent } = useContext(AnalyticsStateContext as any);
  const [formState, setFormState]: [FormState, (state: FormState) => void] = useState(
    FormState.ENTER_CREDENTIALS as FormState
  );
  const [title, setTitle] = useState('');
  const { saveAnalytics } = useContext(EnhancedAnalyticsContext as any);

  const errorMessage = error.length > 0 && (
    <Alert status='error'>
      <AlertIcon />
      {error}
    </Alert>
  );

  function routeToSlug() {
    let page = Router?.router?.query?.page;

    // Add XSS protection against /login?page=javascript:alert(55)
    if (page[0] !== '/') {
      page = `/${page}`;
    }

    Router.push((page && JSON.stringify(page).toString().replaceAll('"', '')) || '/');
  }

  useEffect(() => {
    if (loginType) {
      if (loginType === 'Restricted' || loginRequired) {
        setFormState(FormState.ENTER_CREDENTIALS);
      } else if (loginType === 'Registration') {
        setFormState(FormState.LOGIN);
      } else if (loginType === 'Open') {
        routeToSlug();
      }
    }
  }, [loginType]);

  useEffect(() => {
    setColorMode(darkMode ? 'dark' : 'light');
  }, []);

  async function onAddCredentials(err: string, user: any) {
    setTmpUser(user);
    const userId = await setUserSession(user.fName, user.lName, user.email, AccessLevel.RESTRICTED);

    await saveAnalytics(Action.SIGN_IN, Category.USER_EVENT, 'continue', {
      id: userId,
      fName: user.fName,
      lName: user.lName,
      email: user.email,
      accessLevel: AccessLevel.RESTRICTED
    });
    await signUpEvent({ email: user.email }).then(() => {
      routeToSlug();
    });
  }

  async function onLogin(err: string, user: any) {
    setTmpUser(user);

    if (err) {
      if (err === 'FORGOT_PASSWORD') {
        setFormState(FormState.FORGOT_PASSWORD);
        return;
      }
      if (err === 'NEW_PASSWORD_REQUIRED') {
        setFormState(FormState.NEW_PASSWORD);
        return;
      }
      if (err === 'PASSWORD_VERIFIER' || err === 'PasswordResetRequiredException') {
        setError('Password reset required. Please reset your password before attempting to sign in.');
        return;
      }
      if (err === 'UserNotConfirmedException') {
        setFormState(FormState.CONFIRM_USER);
        return;
      }

      log.error({ action: 'login:error', error: { name: '', message: err } });
      setError(err);
      setFormState(FormState.LOGIN);
      return;
    }
    const userId = await setUserSession(
      user.username.slice(0, user.username.indexOf('@')),
      ' ',
      user.attributes.email,
      AccessLevel.REGISTRATION,
      user.attributes.sub
    );

    // await saveAnalytics(Action.SIGN_IN, Category.USER_EVENT, 'signIn');
    await saveAnalytics(Action.SIGN_IN, Category.USER_EVENT, 'continue', {
      id: userId,
      fName: user.username.slice(0, user.username.indexOf('@')),
      lName: ' ',
      email: user.attributes.email,
      accessLevel: AccessLevel.REGISTRATION
    });

    routeToSlug();
  }

  async function onNewPassword(err: string) {
    if (err) {
      log.error({ action: 'onNewPassword:error', error: { name: '', message: err } });
      setError(err);
      setFormState(FormState.LOGIN);
      return;
    }

    routeToSlug();
  }

  async function onPasswordForgot(err: string, data?: { username: string }) {
    if (err) {
      log.error({ action: 'onPasswordForgot:error', error: { name: '', message: err } });
      setError(err);
    }

    if (!data) {
      setFormState(FormState.LOGIN);
      return;
    }

    setTmpUser(data);

    setFormState(FormState.RESET_PASSWORD);
  }

  async function onPasswordReset(err: string) {
    if (err) {
      log.error({ action: 'onPasswordReset:error', error: { name: '', message: err } });
      setError(err);
    }

    setFormState(FormState.LOGIN);
  }

  useEffect(() => {
    switch (formState) {
      case FormState.ENTER_CREDENTIALS:
        setTitle('Sign in to continue');
        break;
      case FormState.LOGIN:
        setTitle('Sign in to continue');
        break;
      case FormState.NEW_PASSWORD:
        setTitle('New password');
        break;
      case FormState.FORGOT_PASSWORD:
        setTitle('Forgot password');
        break;
      case FormState.RESET_PASSWORD:
        setTitle('Forgot password');
        break;
      case FormState.CONFIRM_USER:
        setTitle('User confirmation');
        break;
      default:
        setTitle('');
    }
  }, [formState]);

  return (
    <Box
      minH='100vh'
      backgroundColor={bgColor || 'gray.50'}
      backgroundImage={`url('${bgImg || ''}')`}
      backgroundPosition='top'
      backgroundRepeat={bgRepeat ? 'repeat-y' : 'no-repeat'}
    >
      {bannerImg && (
        <Center>
          <Box position='relative' textAlign='center'>
            <Image src={bannerImg || ''} borderRadius='3' width='100%' />
          </Box>
        </Center>
      )}

      <Center>
        <Box
          minWidth={{ base: '100%', sm: 'md' }}
          maxW={['90%', '90%', '70%', '40%']}
          px='4'
          pt='8'
          mx='auto'
          // boxShadow='xl'
          borderRadius='3px'
          // backgroundColor='gray.50'
          backgroundColor={useColorModeValue('whiteAlpha.600', 'whiteAlpha.400')}
          boxShadow={useColorModeValue('xl', 'dark-lg')}
        >
          <Center bg='transparent' mb='4'>
            <Text fontSize='4xl' color={useColorModeValue('gray.700', 'white.400')} fontWeight='medium'>
              {title}
            </Text>
          </Center>

          <Box p='3' pt='30' pb='30'>
            {errorMessage}

            {formState === FormState.ENTER_CREDENTIALS ? <CredentialsForm callback={onAddCredentials} /> : null}

            {formState === FormState.LOGIN ? <LoginForm callback={onLogin} /> : null}

            {formState === FormState.NEW_PASSWORD ? <NewPasswordForm user={tmpUser} callback={onNewPassword} /> : null}

            {formState === FormState.CONFIRM_USER ? <VerifyCodeForm user={tmpUser} /> : null}

            {formState === FormState.FORGOT_PASSWORD ? <ForgotPasswordForm callback={onPasswordForgot} /> : null}

            {formState === FormState.RESET_PASSWORD ? (
              <ResetPasswordForm callback={onPasswordReset} user={tmpUser} />
            ) : null}
          </Box>
        </Box>
      </Center>
    </Box>
  );
}

export default withLogger(Login, 'page:login');
